import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { b2bApi, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import { Stat, StatusPill, Card, fmtDate, fmtDateTime } from "@/components/common/Stat";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Layers, UserCheck, UserPlus, CreditCard, ExternalLink } from "lucide-react";

const eur = (cents, cur = "eur") => new Intl.NumberFormat("nl-NL", { style: "currency", currency: cur.toUpperCase() }).format((cents || 0) / 100);

function PlanPicker({ orgId, onStarted }) {
  const t = useT();
  const plans = useQuery({ queryKey: ["b2b-plans"], queryFn: async () => (await b2bApi.get("/b2b/billing/plans")).data, retry: false });
  const [planId, setPlanId] = useState("business_monthly");
  const [seats, setSeats] = useState(10);
  const [busy, setBusy] = useState(false);
  const list = plans.data?.plans || [];
  const plan = list.find((p) => p.plan_id === planId) || list[0];
  const total = plan?.price_available ? plan.unit_amount * Math.max(1, Number(seats) || 0) : null;

  const start = async () => {
    setBusy(true);
    try {
      const r = await b2bApi.post("/b2b/billing/checkout", { org_id: orgId, plan_id: planId, seats: Number(seats), origin_url: window.location.origin });
      onStarted?.(r.data);
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <Card title={t("b2b.billing.choosePlan")} testId="billing-plan-picker">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        {list.map((p) => (
          <button key={p.plan_id} type="button" onClick={() => setPlanId(p.plan_id)} data-testid={`billing-plan-${p.plan_id}`}
            className={`text-left rounded-2xl border p-4 transition-colors ${planId === p.plan_id ? "border-sage-500 bg-sage-100/50" : "border-border bg-white hover:bg-cream-50"}`}>
            <div className="text-[11px] uppercase tracking-[0.15em] text-ink-500 font-semibold">{p.interval === "year" ? t("b2b.billing.yearly") : t("b2b.billing.monthly")}</div>
            <div className="font-heading text-2xl text-ink-900 mt-1">{p.price_available ? eur(p.unit_amount, p.currency) : t("b2b.billing.contact")}</div>
            <div className="text-xs text-ink-500">{p.interval === "year" ? t("b2b.billing.perSeatYear") : t("b2b.billing.perSeat")} · {t("b2b.billing.excl")}</div>
          </button>
        ))}
        {!list.length && <div className="text-sm text-ink-500">{plans.isLoading ? t("common.loading") : t("common.empty")}</div>}
      </div>
      <label className="block text-sm text-ink-700 mb-1" htmlFor="seat-count">{t("b2b.billing.seatCount")}</label>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <Input id="seat-count" type="number" min={1} max={plans.data?.max_seats || 5000} value={seats} onChange={(e) => setSeats(e.target.value)} className="h-11 rounded-xl bg-white max-w-[160px]" data-testid="billing-seats-input" />
        <div className="text-sm text-ink-700" data-testid="billing-total">
          {t("b2b.billing.total")}: <span className="font-semibold text-ink-900">{total != null ? eur(total, plan.currency) : "—"}</span> / {plan?.interval === "year" ? t("b2b.billing.yearly").toLowerCase() : t("b2b.billing.monthly").toLowerCase()} · {t("b2b.billing.excl")}
        </div>
      </div>
      <Button onClick={start} disabled={busy || !plan?.price_available || !(Number(seats) >= 1)} className="mt-4 rounded-full bg-sage-500 hover:bg-sage-600 text-white h-11 px-6" data-testid="billing-start-checkout">
        <CreditCard className="h-4 w-4 mr-2" /> {busy ? t("common.loading") : t("b2b.billing.startCheckout")}
      </Button>
      <div className="mt-5 rounded-2xl bg-cream-50 border border-border p-4">
        <div className="font-medium text-ink-900 text-sm">{t("b2b.billing.enterprise")}</div>
        <p className="text-xs text-ink-500 mt-1">{t("b2b.billing.enterpriseBody")}</p>
        <a href={`mailto:${plans.data?.enterprise?.email || "contact@innercompass.tech"}`} className="text-sm text-sage-600 font-semibold mt-2 inline-block" data-testid="billing-enterprise-contact">{plans.data?.enterprise?.email || "contact@innercompass.tech"}</a>
      </div>
    </Card>
  );
}

/** Licence state + management for one organisation. Reused by HR billing page and Platform Owner org detail. */
export function LicensePanel({ orgId, compact = false }) {
  const t = useT();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["b2b-license", orgId], queryFn: async () => (await b2bApi.get(`/b2b/billing/org/${orgId}`)).data, enabled: !!orgId, retry: false });
  const audit = useQuery({ queryKey: ["b2b-org-audit", orgId], queryFn: async () => (await b2bApi.get(`/b2b/org/${orgId}/audit`, { params: { limit: 10 } })).data, enabled: !!orgId && !compact, retry: false });
  const [seats, setSeats] = useState("");
  const [busy, setBusy] = useState(false);
  const lic = q.data?.license;
  const refresh = () => qc.invalidateQueries({ queryKey: ["b2b-license", orgId] });

  const act = async (fn) => { setBusy(true); try { await fn(); refresh(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const openPortal = () => act(async () => { const r = await b2bApi.post(`/b2b/billing/org/${orgId}/portal`, { origin_url: window.location.origin }); window.location.href = r.data.url; });
  const saveSeats = () => act(async () => { await b2bApi.post(`/b2b/billing/org/${orgId}/seats`, { seats: Number(seats) }); toast.success(t("common.saved")); setSeats(""); });
  const cancel = () => act(async () => { await b2bApi.post(`/b2b/billing/org/${orgId}/cancel`); toast.success(t("common.saved")); });
  const resume = () => act(async () => { await b2bApi.post(`/b2b/billing/org/${orgId}/resume`); toast.success(t("common.saved")); });

  if (!orgId) return <Card testId="billing-no-org"><div className="text-sm text-ink-500">{t("b2b.billing.noOrg")}</div></Card>;
  if (q.isLoading) return <Card testId="billing-loading"><div className="text-sm text-ink-500">{t("common.loading")}</div></Card>;
  if (q.isError) return <Card testId="billing-error"><div className="text-sm text-rust-600">{errMsg(q.error)}</div></Card>;

  const active = lic && ["active", "grace"].includes(lic.entitlement);
  return (
    <div className="space-y-4" data-testid="license-panel">
      <Card title={t("b2b.billing.title")} testId="license-card"
        actions={<StatusPill status={lic?.status} label={t(`b2b.billing.states.${lic?.status || "none"}`)} testId="license-status" />}>
        {lic?.last_payment_status === "failed" && <div className="mb-3 rounded-xl bg-rust-500/10 border border-rust-500/20 text-rust-600 text-sm px-3 py-2" data-testid="license-payment-failed">{t("b2b.billing.paymentFailed")}</div>}
        <div className="grid grid-cols-3 gap-3">
          <Stat icon={Layers} label={t("b2b.billing.purchased")} value={lic?.seats_purchased ?? 0} testId="license-seats-purchased" accent="bg-cream-50" />
          <Stat icon={UserCheck} label={t("b2b.billing.assigned")} value={lic?.seats_assigned ?? 0} testId="license-seats-assigned" accent="bg-cream-50" />
          <Stat icon={UserPlus} label={t("b2b.billing.available")} value={lic?.seats_available ?? 0} testId="license-seats-available" accent="bg-cream-50" />
        </div>
        {lic?.has_subscription && (
          <div className="mt-4 text-sm text-ink-700 flex flex-wrap gap-x-6 gap-y-1">
            <span>{lic.interval === "year" ? t("b2b.billing.yearly") : t("b2b.billing.monthly")}</span>
            <span>{lic.cancel_at_period_end ? t("b2b.billing.cancelsAt") : t("b2b.billing.renews")}: <b>{fmtDate(lic.current_period_end)}</b></span>
          </div>
        )}
        {active && (
          <div className="mt-5 space-y-3">
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <Input type="number" min={1} value={seats} onChange={(e) => setSeats(e.target.value)} placeholder={String(lic.seats_purchased)} className="h-11 rounded-xl bg-white max-w-[160px]" data-testid="license-seats-input" />
              <Button onClick={saveSeats} disabled={busy || !seats} variant="outline" className="rounded-full h-11" data-testid="license-seats-save">{t("b2b.billing.saveSeats")}</Button>
              <span className="text-xs text-ink-500">{t("b2b.billing.seatsHint")}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={openPortal} disabled={busy} className="rounded-full bg-ink-900 hover:bg-ink-700 text-white h-11" data-testid="license-portal"><ExternalLink className="h-4 w-4 mr-2" />{t("b2b.billing.manage")}</Button>
              {lic.cancel_at_period_end
                ? <Button onClick={resume} disabled={busy} variant="outline" className="rounded-full h-11" data-testid="license-resume">{t("b2b.billing.resume")}</Button>
                : <Button onClick={cancel} disabled={busy} variant="outline" className="rounded-full h-11 text-rust-600 border-rust-500/30" data-testid="license-cancel">{t("b2b.billing.cancel")}</Button>}
            </div>
          </div>
        )}
        {!active && lic?.has_customer && lic?.has_subscription && (
          <Button onClick={openPortal} disabled={busy} variant="outline" className="mt-4 rounded-full h-11" data-testid="license-portal-inactive"><ExternalLink className="h-4 w-4 mr-2" />{t("b2b.billing.manage")}</Button>
        )}
      </Card>
      {!active && !compact && <PlanPicker orgId={orgId} />}
      {!compact && (
        <Card title={t("b2b.billing.history")} testId="billing-history">
          <div className="divide-y divide-border">
            {(audit.data?.items || []).map((a) => (
              <div key={a.id} className="py-2 text-sm flex justify-between gap-3"><span className="font-mono text-xs text-ink-900">{a.action}</span><span className="text-xs text-ink-500">{fmtDateTime(a.created_at)}</span></div>
            ))}
            {!(audit.data?.items || []).length && <div className="text-sm text-ink-500 py-2">{t("common.empty")}</div>}
          </div>
        </Card>
      )}
    </div>
  );
}

/** HR / organisation admin page: /admin/billing. Handles Stripe return (?session_id) by polling the backend — never trusts the redirect. */
export default function Billing() {
  const t = useT();
  const { orgId } = useOutletContext();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [polling, setPolling] = useState(!!sessionId);

  useEffect(() => {
    if (!sessionId) return;
    if (sp.get("cancelled")) return;
    let tries = 0; let alive = true;
    const tick = async () => {
      try {
        const r = await b2bApi.get(`/b2b/billing/checkout/${sessionId}/status`);
        if (!alive) return;
        if (r.data.payment_status === "paid") { toast.success(t("b2b.billing.confirmed")); setPolling(false); qc.invalidateQueries({ queryKey: ["b2b-license"] }); setSp({}, { replace: true }); return; }
        if (["expired", "failed"].includes(r.data.payment_status)) { setPolling(false); setSp({}, { replace: true }); return; }
      } catch (e) { if (!alive) return; }
      if (++tries < 10) setTimeout(tick, 2000); else setPolling(false);
    };
    tick();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => { if (sp.get("cancelled")) { toast.message(t("b2b.billing.cancelled")); setSp({}, { replace: true }); } }, [sp, setSp, t]);

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("nav.business")} title={t("b2b.billing.title")} subtitle={t("b2b.billing.subtitle")} />
      {polling && <div className="rounded-2xl bg-sage-100/60 border border-sage-500/20 text-sage-700 text-sm px-4 py-3 animate-pulse" data-testid="billing-polling">{t("b2b.billing.checking")}</div>}
      <LicensePanel orgId={orgId} />
    </div>
  );
}
